import type { UserRole } from '@shift/contracts';
import { canUseClientApp } from '../entities/session/roles.js';

export type NavigationTarget = { name: string; query?: Record<string, string> };

export type NavigationDecision =
  | { kind: 'allow' }
  | { kind: 'redirect'; to: NavigationTarget }
  /**
   * The account has no business in this app at all. End the session, then go
   * to `to` — or let the request through when `to` is absent, which is how a
   * request already heading for the login page is answered without redirecting
   * it to the page it is already on.
   */
  | { kind: 'end-session'; to?: NavigationTarget };

export interface NavigationRequest {
  routeName: string | undefined;
  isPublic: boolean;
  fullPath: string;
  isAuthenticated: boolean;
  roles: readonly UserRole[];
  /** The roles this particular route accepts. */
  allowedRoles: readonly UserRole[];
}

/**
 * Who may go where, as a decision rather than a side effect.
 *
 * Separated from the router so it can be tested without a DOM, which is this
 * app's convention for its framework-free slices — and because the bug it
 * exists to prevent is a bug of *reasoning*, not of navigation: sending a
 * role-mismatched user home is right until home is somewhere they cannot be
 * either, at which point the fallback fails its own check and the browser
 * spins between two answers to the same question.
 */
export const decideNavigation = (request: NavigationRequest): NavigationDecision => {
  // Answered before anything else, and by ending the session rather than by
  // moving them: a worker or a firm's dispatcher has no page in this app, so
  // there is nowhere to send them that will not ask this again.
  if (request.isAuthenticated && !canUseClientApp(request.roles)) {
    // Ending it is not optional, even here. Allowing the login page through
    // with the session intact would leave the refresh cookie live, and the
    // next load would restore the account and ask this question again.
    //
    // Already on the way there: end the session and let the request continue,
    // rather than redirecting the login page to itself.
    return request.routeName === 'login'
      ? { kind: 'end-session' }
      : { kind: 'end-session', to: { name: 'login', query: { reason: 'wrong-app' } } };
  }

  if (request.isPublic) {
    const isAuthEntry = request.routeName === 'login' || request.routeName === 'register';
    return isAuthEntry && request.isAuthenticated
      ? { kind: 'redirect', to: { name: 'home' } }
      : { kind: 'allow' };
  }

  if (!request.isAuthenticated) {
    return { kind: 'redirect', to: { name: 'login', query: { redirect: request.fullPath } } };
  }

  // A client who cannot see *this* page is sent home, which they can. Bouncing
  // an authenticated user to a login form reads as a session failure and
  // invites them to try their password again.
  const permitted = request.roles.some((role) => request.allowedRoles.includes(role));
  return permitted ? { kind: 'allow' } : { kind: 'redirect', to: { name: 'home' } };
};
